import { useState } from 'react'
import { parseAsInteger, useQueryStates } from 'nuqs'
import { toast } from 'sonner'
import AllRoles from '../../components/AllRoles/AllRoles'
import Pagination from '@/shared/components/Pagination/Pagination'
import ActionHeader from '@/shared/components/ActionHeader/ActionHeader'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog/ConfirmDialog'
import { Loader } from '@/shared/components/Loader/Loader'
import { Separator } from '@/ui/separator'
import { useDebounce } from '@/shared/hooks'
import { useRolesQuery } from '../../queries/useRoleQueries'
import { useRestoreRoleMutation } from '../../mutations/useRoleMutations'
import type { RoleListItem } from '../../services/roles/roles.types'
import styles from '../RolePage/Rolepage.module.scss'

export default function RoleDeletedPage() {
  const [{ page, pageSize }, setPagination] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(10),
  })

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 400)

  const [selectedRole, setSelectedRole] = useState<RoleListItem | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data, isLoading, isError, isFetching, refetch } = useRolesQuery({
    isActive: '0',
    pageNumber: page,
    limit: pageSize,
    searchName: debouncedSearch || undefined,
    sortBy: 'created_date',
    sortOrder: 'desc',
  })

  const roles = data?.roles ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const restoreRoleMutation = useRestoreRoleMutation({
    onSuccess: () => toast.success('Role restored successfully'),
    onError: (error) => toast.error(error.message),
  })

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (page !== 1) setPagination({ page: 1 })
  }

  const handleRestore = (role: RoleListItem) => {
    setSelectedRole(role)
    setConfirmOpen(true)
  }

  const closeConfirm = () => {
    setConfirmOpen(false)
    setSelectedRole(null)
  }

  const handleConfirmRestore = () => {
    if (selectedRole) {
      restoreRoleMutation.mutate(selectedRole.id)
    }
    closeConfirm()
  }

  return (
    <div>
      {isLoading ? (
        <Loader text="Loading deleted roles..." />
      ) : isError ? (
        <p className={styles.error}>Error loading deleted roles</p>
      ) : (
        <>
          <div className={styles.actionHeaderWrapper}>
            <ActionHeader
              title="Deleted Roles"
              search={search}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search Roles"
              actions={[
                {
                  icon: 'refresh',
                  onClick: () => refetch(),
                  variant: 'icon',
                  ariaLabel: 'Refresh',
                  key: 'refresh-deleted-roles',
                },
              ]}
            />
          </div>
          <Separator />
          <div className={styles.tableWrapper}>
            {roles.length === 0 ? (
              <p className={styles.error}>
                {isFetching ? 'Loading roles...' : 'No deleted roles found'}
              </p>
            ) : (
              <>
                <AllRoles
                  mode="deleted"
                  roles={roles}
                  onRestore={handleRestore}
                />
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalRecords={total}
                  rowsPerPage={pageSize}
                  onPageChange={(next) => setPagination({ page: next })}
                  onRowsPerPageChange={(size) =>
                    setPagination({ pageSize: size, page: 1 })
                  }
                />
              </>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Restore role"
        message={
          selectedRole
            ? `Restore role "${selectedRole.title}" back to active roles?`
            : 'Restore this role?'
        }
        confirmText="Restore"
        cancelText="Cancel"
        onConfirm={handleConfirmRestore}
        onCancel={closeConfirm}
      />
    </div>
  )
}
